import React, { useMemo, useState } from 'react';

import { Grid, TextField, Button, Box, CircularProgress } from '@material-ui/core';

import { Formik, Form, Field } from 'formik';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import helpers from 'app/utils/helpers';
import { userValidationSchema } from 'app/utils/forms/validationsSchemas';
import notification from 'app/config/notification';
import userEndpoints from 'app/api/services/userEndpoints';
import { errorHandler } from 'app/utils/errorHandler';

function UserForm(props) {
    const { mainColor, intl, classes, user, closeDrawer, setRefreshUsers } = props;

    const userLogged = useSelector((state) => state.auth.user);

    const [loading, setLoading] = useState(false);
    const [deCpfCnpj, setDeCpfCnpj] = useState(() => {
        if (user && user.deCpfCnpj && user.deCpfCnpj !== '') return helpers.formatarDocumento(user.deCpfCnpj);
        return '';
    });

    const canEdit = useMemo(() => {
        return true;
    }, []);

    const editing = useMemo(() => {
        return user && Object.keys(user).length > 0;
    }, [user]);

    const canEditField = useMemo(() => {
        return !editing;
    }, [editing]);

    const initialValues = useMemo(() => {
        if (editing) {
            return user;
        }
        return {
            nmUsuario: '',
            deEmail: '',
            deCpfCnpj: '',
        };
    }, [editing, user]);

    const onSave = async (values) => {
        setLoading(true);
        const valuesCopy = { ...values };
        valuesCopy.admin = false;
        valuesCopy.boAtivo = true;
        valuesCopy.cdEmpresaPadrao = userLogged.cdEmpresaSelecionada;

        if (userLogged.boVistoriador) {
            valuesCopy.boVistoriador = userLogged.boVistoriador;
        }

        if (userLogged.username === helpers.somenteAlfanumericos(valuesCopy.deCpfCnpj)) {
            notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.USER_SAVE_BLOCKED_SAME_IDENTIFIER' }), {
                toastId: 'USER_SAVE_BLOCKED_SAME_IDENTIFIER',
                type: 'error',
            });
            setLoading(false);
            return;
        }

        try {
            const response = await userEndpoints.saveUser(valuesCopy);
            if (response.status === 200) {
                closeDrawer(false);
                setRefreshUsers(Math.random());
                notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.USER_SUCCESSFULLY_SAVED' }), {
                    toastId: 'USER_SUCCESSFULLY_SAVED',
                    type: 'success',
                });
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        }
        setLoading(false);
    };

    const onSubmit = () => {};

    const onCancel = () => {
        closeDrawer(false);
    };

    const validateDocument = (value) => {
        let error;
        if (!helpers.validateCpfCnpj(value)) {
            error = 'Documento inválido';
        }
        return error;
    };

    const handleChangeDocument = (e, values) => {
        const number = helpers.formatarDocumento(e.target.value);
        setDeCpfCnpj(number);
        values.deCpfCnpj = number;
    };

    return (
        <div className={classes.formContainer}>
            <Formik
                enableReinitialize={true}
                initialValues={initialValues ? initialValues : {}}
                validateOnBlur={false}
                validationSchema={userValidationSchema}
                validateOnChange={false}
                onSubmit={(values) => {
                    onSubmit(values);
                }}>
                {(props) => {
                    const { values, errors, touched } = props;
                    return (
                        <Form className={classes.containerForm}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={12}>
                                    <Field
                                        name="nmUsuario"
                                        label={'Nome*'}
                                        variant="filled"
                                        margin="dense"
                                        disabled={!canEdit}
                                        fullWidth
                                        error={!!errors.nmUsuario}
                                        helperText={errors.nmUsuario && touched.nmUsuario ? errors.nmUsuario : ''}
                                        as={TextField}
                                        inputProps={{
                                            maxLength: 150,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    {canEditField && (
                                        <Field
                                            name="deCpfCnpj"
                                            label="CPF/CNPJ"
                                            variant="filled"
                                            value={deCpfCnpj}
                                            fullWidth
                                            error={!!errors.deCpfCnpj}
                                            helperText={errors.deCpfCnpj && touched.deCpfCnpj ? errors.deCpfCnpj : ''}
                                            onChange={(e) => handleChangeDocument(e, values)}
                                            as={TextField}
                                            inputProps={{
                                                maxLength: 18,
                                            }}
                                            validate={validateDocument}
                                        />
                                    )}
                                    {!canEditField && (
                                        <TextField
                                            label="CPF*"
                                            value={values.deCpfCnpj}
                                            variant="filled"
                                            margin="dense"
                                            disabled
                                            fullWidth
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Field
                                        name="deEmail"
                                        label={'E-mail*'}
                                        variant="filled"
                                        margin="dense"
                                        fullWidth
                                        disabled={!canEdit}
                                        error={!!errors.deEmail}
                                        helperText={errors.deEmail && touched.deEmail ? errors.deEmail : ''}
                                        as={TextField}
                                        inputProps={{
                                            maxLength: 150,
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            {canEdit && (
                                <Grid container spacing={3}>
                                    <div className={classes.actionsContainer}>
                                        {loading && (
                                            <div className={classes.centerProgress}>
                                                <CircularProgress size={20} />
                                            </div>
                                        )}
                                        {!loading && (
                                            <>
                                                <Grid item xs={12} sm={4}>
                                                    <Button className={classes.actionsBtns} onClick={() => onCancel()}>
                                                        <FormattedMessage id="LABELS.COMMONS.CANCEL" />
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Button
                                                        variant="contained"
                                                        color={mainColor ? mainColor : 'primary'}
                                                        className={classes.actionsBtns}
                                                        disableElevation
                                                        onClick={() => {
                                                            props.validateForm().then((res) => {
                                                                if (Object.keys(res).length <= 0) onSave(props.values);
                                                                else {
                                                                    notification(
                                                                        'O(s) campo(s) sinalizado(s) precisam de revisão.',
                                                                        {
                                                                            toastId: 'VALIDATION',
                                                                            type: 'error',
                                                                        },
                                                                    );
                                                                }
                                                            });
                                                        }}>
                                                        <Box className={classes.colorWhite}>
                                                            <FormattedMessage id="LABELS.COMMONS.SAVE" />
                                                        </Box>
                                                    </Button>
                                                </Grid>
                                            </>
                                        )}
                                    </div>
                                </Grid>
                            )}
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
}
export default injectIntl(UserForm);
